/**
 * Reports Domain - Composition Root
 *
 * Single entry point for all domain dependencies.
 * Implements singleton pattern with explicit initialization.
 *
 * Flow:
 * 1. App/bootstrap.ts calls initReportsComposition() when user is authenticated
 * 2. Hooks call getBlocksUseCases(), getFiltersUseCases(), or getExportUseCases()
 * 3. Use cases are already composed with their dependencies
 *
 * @module @reports/composition
 */

import type { AxiosInstance } from 'axios';
import type { AuthService } from 'types/AuthService';
import type { ReportsComposition, BlocksUseCases, FiltersUseCases, ExportUseCases } from './types';
import { createBlocksUseCases } from './factories/createBlocksUseCases';
import { createFiltersUseCases } from './factories/createFiltersUseCases';
import { createExportUseCases } from './factories/createExportUseCases';

export type { ReportsComposition, BlocksUseCases, FiltersUseCases, ExportUseCases } from './types';

/**
 * Creates a new complete composition of the domain.
 * Internal use - consumers should use init/get functions.
 */
const createReportsComposition = (httpClient: AxiosInstance, authService: AuthService): ReportsComposition => ({
  blocksUseCases: createBlocksUseCases(httpClient, authService),
  filtersUseCases: createFiltersUseCases(httpClient, authService),
  exportUseCases: createExportUseCases(httpClient, authService),
});

let composition: ReportsComposition | null = null;

/**
 * Initializes the Reports domain Composition Root.
 * MUST be called ONCE during app bootstrap,
 * BEFORE any hook attempts to use the use cases.
 *
 * @param httpClient - Axios instance for reports API
 * @param authService - Authentication service from useAuth hook
 *
 * @example
 * // In App/bootstrap.ts
 * import { reportsApi } from 'domains/reports/infrastructure/api/axiosReport';
 * import { initReportsComposition } from 'domains/reports';
 *
 * const bootstrapApp = (authService) => {
 *   initReportsComposition(reportsApi, authService);
 * };
 */
export const initReportsComposition = (httpClient: AxiosInstance, authService: AuthService): void => {
  if (composition !== null) {
    return;
  }

  composition = createReportsComposition(httpClient, authService);
};

/**
 * Gets the Composition Root singleton.
 * MUST be called AFTER initReportsComposition.
 * @throws Error if not initialized
 * @example
 * const { blocksUseCases } = getReportsComposition();
 */
export const getReportsComposition = (): ReportsComposition => {
  if (composition === null) {
    throw new Error(
      '[Reports] Composition not initialized.\n' +
        'Call initReportsComposition(httpClient, authService) in App/bootstrap.ts before using hooks.',
    );
  }

  return composition;
};

/**
 * Reset for testing.
 * @internal
 */
export const resetReportsComposition = (): void => {
  composition = null;
};

/**
 * Gets block use cases (chart and table blocks).
 *
 * @example
 * // In a hook
 * const { fetchChartBlock, fetchTableBlock } = getBlocksUseCases();
 *
 * return useQuery({
 *   queryFn: () => fetchChartBlock.execute(filters, endpoint),
 * });
 */
export const getBlocksUseCases = (): BlocksUseCases => {
  return getReportsComposition().blocksUseCases;
};

/**
 * Gets filter use cases.
 *
 * @example
 * const { fetchFilterCatalog, saveAppliedFilters } = getFiltersUseCases();
 */
export const getFiltersUseCases = (): FiltersUseCases => {
  return getReportsComposition().filtersUseCases;
};

/**
 * Gets export use cases.
 *
 * @example
 * const { exportReport } = getExportUseCases();
 */
export const getExportUseCases = (): ExportUseCases => {
  return getReportsComposition().exportUseCases;
};
